import { ConfirmDialog } from "@/components/dialog/confirmation";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  deleteEnrollment,
  updateEnrollmentStatus,
} from "@/store/features/enroll-slice";
import { dispatch, useSelector } from "@/store/store";
import { ColumnDef } from "@tanstack/react-table";
import { FilterIcon, MoreHorizontal, X } from "lucide-react";
import moment from "moment";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DataTableColumnHeader } from "./data-table-column-header";

export type Enquiry = {
  id: string;
  createdAt: string;
  fullName: string;
  email: string;
  mobile: string;
  message: string;
  address: string;
  academicLevel: string;
  is_enrolled: boolean;
  is_compelted: boolean;
  course: {
    id: string;
    name: string;
    duration: string;
    status: "active" | "inactive";
    price: number;
  };
};

const ActionsCell = ({ enrollment }: { enrollment: Enquiry }) => {
  const navigate = useNavigate();

  const [open, setOpen] = useState<boolean>(false); // Control dialog state

  const loading = useSelector((state) => state.enrollment.isLoading);

  const handleDelete = (id: string) => {
    dispatch(
      deleteEnrollment({
        id,
        onClose: () => setOpen(false),
      })
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="h-8 w-8 p-0">
          <span className="sr-only">Open menu</span>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => navigate(`/enquiry/${enrollment.id}`)}>
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            setOpen(true);
          }}
        >
          Delete
        </DropdownMenuItem>

        <Dialog onOpenChange={setOpen} open={open}>
          <DialogTrigger asChild></DialogTrigger>
          <ConfirmDialog
            loading={loading}
            title={`Delete ${enrollment?.fullName}?`}
            onOk={() => handleDelete(enrollment?.id)}
            onClose={() => setOpen(false)}
          />
        </Dialog>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const FilterSelect = ({ column, options }: { column: any; options: any[] }) => {
  const selectedValues = (column.getFilterValue() as any[]) || [];

  const handleSelect = (option: any) => {
    const currentFilters = selectedValues || [];

    const isSelected = currentFilters.some((item) => item === option);
    const newFilters = isSelected
      ? currentFilters.filter((item) => item !== option)
      : [...currentFilters, option];

    column.setFilterValue(newFilters.length ? newFilters : undefined);
  };

  const handleReset = () => {
    column.setFilterValue(undefined);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
          <FilterIcon
            size={14}
            className={`${
              selectedValues.length ? "text-primary" : "text-gray-500"
            } font-bold`}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-fit">
        <DropdownMenuLabel className="flex items-center justify-between">
          Filter by
          {selectedValues.length > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4 p-0"
              onClick={handleReset}
            >
              <X size={12} className="text-gray-500" />
            </Button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-96 overflow-y-auto">
          {options.map((option) => (
            <>
              <DropdownMenuCheckboxItem
                key={option}
                checked={selectedValues.some((item) => item === option)}
                onCheckedChange={() => handleSelect(option)}
                className="capitalize"
              >
                {option}
              </DropdownMenuCheckboxItem>
              <Separator />
            </>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const FilterCourse = ({ column }: { column: any }) => {
  // Get courses from Redux store
  const courses = useSelector((state) => state.course.data?.data || []);

  // Extract course names
  const courseOptions = useMemo(() => courses.map((c) => c.name), [courses]);

  return <FilterSelect column={column} options={courseOptions} />;
};

const SwitchAction = ({ enquiry }: { enquiry: Enquiry }) => {
  const [open, setOpen] = useState<boolean>(false); // Control dialog state

  const loading = useSelector((state) => state.enrollment.isLoading);
  const updateEnroll = (value: boolean, enrollId: string) => {
    dispatch(
      updateEnrollmentStatus({
        data: {
          is_enrolled: value,
        },
        id: enrollId,
        onClose: () => setOpen(false),
      })
    );
  };

  return (
    <>
      <Switch
        checked={enquiry?.is_enrolled}
        onCheckedChange={() => setOpen(true)}
      />

      <Dialog onOpenChange={setOpen} open={open}>
        <DialogTrigger asChild></DialogTrigger>
        <ConfirmDialog
          loading={loading}
          title={`${enquiry?.is_enrolled ? "Un-Enroll" : "Enroll"} ${
            enquiry?.fullName
          }?`}
          onOk={() => updateEnroll(!enquiry?.is_enrolled, enquiry?.id)}
          onClose={() => setOpen(false)}
        />
      </Dialog>
    </>
  );
};

export const columns: ColumnDef<Enquiry>[] = [
  {
    accessorKey: "fullName",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Student's Name" />
    ),
    cell: ({ row }) => <div>{row.getValue("fullName")}</div>,
  },

  {
    accessorKey: "email",
    header: ({ column }) => {
      return (
        <button
          className="flex items-center font-bold"
          // onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Email
          {/* <ArrowUpDown className="ml-2 h-4 w-4" /> */}
        </button>
      );
    },
    cell: ({ row }) => <div>{row.getValue("email")}</div>,
  },

  {
    accessorKey: "mobile",
    header: ({ column }) => {
      return (
        <button
          className="flex items-center font-bold"
          // onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Phone
          {/* <ArrowUpDown className="ml-2 h-4 w-4" /> */}
        </button>
      );
    },
    cell: ({ row }) => <div>{row.getValue("mobile")}</div>,
  },

  {
    accessorKey: "address",
    header: ({ column }) => {
      return (
        <button
          className="flex items-center font-bold"
          // onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Address
          {/* <ArrowUpDown className="ml-2 h-4 w-4" /> */}
        </button>
      );
    },
    cell: ({ row }) => {
      return <div>{row.getValue("address")}</div>;
    },
  },

  {
    id: "course",
    accessorKey: "course",
    header: ({ column }) => {
      return (
        <div className="flex items-center space-x-2">
          <span className="flex items-center font-bold">Course</span>
        </div>
      );
    },
    cell: ({ row }) => {
      return (
        <Link
          to={`/courses/${row?.original?.course?.id}`}
          className="underline"
        >
          {row.original?.course?.name}
        </Link>
      );
    },
  },

  {
    accessorKey: "is_enrolled",
    header: ({ column }) => {
      return (
        <div className="flex items-center space-x-2">
          <span className="flex items-center font-bold">Enrolled?</span>
          {/* <FilterSelect column={column} options={[1, 0]} /> */}
        </div>
      );
    },
    cell: ({ row }) => {
      return <SwitchAction enquiry={row?.original} />;
    },
  },

  {
    accessorKey: "createdAt",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => (
      <div>{moment(row.getValue("createdAt")).format("YYYY-MM-DD")}</div>
    ),
  },
  {
    id: "actions",
    cell: ({ row }) => <ActionsCell enrollment={row.original} />,
  },
];
